import Dexie, { type EntityTable } from "dexie";

/**
 * Lokale IndexedDB der PWA. 5 stores:
 *
 *  - auth     — JWT-pair + user-claims (kein localStorage → XSS-hygiene)
 *  - outbox   — pending mutations für offline-sync (POST/PATCH/DELETE)
 *  - kv       — TanStack-Query-persistence (key/value, last-write-wins)
 *  - rooms    — local-cache (NetworkFirst → fallback)
 *  - entries  — local-cache (NetworkFirst → fallback)
 *
 * Bei DB-schema-änderung MUSS die DB_VERSION bumped werden + eine
 * v<n>.upgrade()-callback registriert. Ohne upgrade verliert Dexie
 * die alten daten silent — siehe https://dexie.org/docs/Tutorial/Design#database-versioning.
 */

export const DB_NAME = "hitzeprotokoll";
export const DB_VERSION = 1;

/**
 * Role-enum mirror der server-side users.role check-constraint
 * (siehe schema/0001_init.up.sql + 0011_traeger_hierarchy.up.sql).
 * Phase 15 added `traeger_admin` als 4. role (T-tier-multi-standort).
 */
export type UserRole = "admin" | "leitung" | "betreuer" | "traeger_admin";

export interface AuthRecord {
  id: "current";
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: number; // unix ms
  user: {
    userId: string;
    orgId: string;
    role: UserRole;
    email?: string;
    /**
     * Phase 15 (ADR-008): set für traeger_admin → traeger-org-id
     * (typischerweise === orgId, selbst-ref). Triggert hierarchical-
     * tenant-routing in /v1/traeger/*-endpoints. Bei anderen rollen
     * leer/undefined.
     */
    parentOrgId?: string;
    /**
     * Phase 18 — lizenz-expiry (unix-seconds, mirror des JWT-claims lic_exp).
     * undefined / 0 = keine enforcement (legacy-tokens vor phase-18-deploy,
     * self-host-installs ohne trial-mechanik). Wert kommt aus access-token
     * beim login + bei refresh-rotation (api-client.tryRefresh).
     */
    licenseExpiresAt?: number;
  };
  loggedInAt: number;
}

export interface OutboxRecord {
  id?: number;
  method: "POST" | "PATCH" | "DELETE";
  url: string; // relative path, prefix mit API_BASE_URL beim send
  body?: string;
  headers?: Record<string, string>;
  createdAt: number;
  lastAttemptAt?: number;
  attempts: number;
  status: "pending" | "in_flight" | "failed";
  errorMessage?: string;
  // Idempotency: opaque uuid the api uses as client_uuid; falls bestehend,
  // server returnt 200 statt 201 → outbox ist safe to retry beliebig oft.
  clientUuid?: string;
}

export interface KVRecord {
  key: string;
  value: unknown;
  updatedAt: number;
}

export interface CachedRoom {
  id: string;
  org_id?: string;
  name: string;
  etage?: string | null;
  kapazitaet?: number | null;
  notiz?: string | null;
  location_note?: string;
  sort_order: number;
  updated_at: number;
  // Original JSON-blob für lookup/sync (nicht alle felder werden gequeried)
  raw: unknown;
}

export interface CachedEntry {
  id: string;
  room_id: string;
  event_at: number; // unix ms
  indoor_temp_c: number;
  confirmed_level: number;
  is_provisional: boolean;
  raw: unknown;
}

export interface SyncMeta {
  key: "last_sync" | "last_dashboard_fetch" | "last_online_at";
  timestamp: number;
}

class HitzeprotokollDB extends Dexie {
  auth!: EntityTable<AuthRecord, "id">;
  outbox!: EntityTable<OutboxRecord, "id">;
  kv!: EntityTable<KVRecord, "key">;
  rooms!: EntityTable<CachedRoom, "id">;
  entries!: EntityTable<CachedEntry, "id">;
  syncMeta!: EntityTable<SyncMeta, "key">;

  constructor() {
    super(DB_NAME);
    this.version(DB_VERSION).stores({
      auth: "id",
      outbox: "++id, status, createdAt",
      kv: "key",
      rooms: "id, sort_order",
      entries: "id, room_id, event_at",
      syncMeta: "key",
    });
  }
}

export const db = new HitzeprotokollDB();

/**
 * Vollständiger reset — DSGVO Art. 17 + iOS-7d-eviction-recovery.
 * Settings-route ruft das auf nach explizitem confirm. Nach erfolg:
 * SW unregister + redirect /login.
 */
export async function wipeLocalData(): Promise<void> {
  await db.delete();
  // Auch die Cache-API leeren (workbox-precache, runtime-caches)
  if (typeof caches !== "undefined") {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  }
  // Re-open damit folge-operations nicht crashen (e.g. login-page lädt erneut)
  await db.open();
}
